import * as React from 'react';
import { BillingPeriod } from 'fs-api-client';
import { createBillingApi } from '../../utils/createApi';
import { Subtract } from '../../utils/types';
import { withLoader, LoaderEnhancerProperties } from './LoaderEnhancer';
import { createDomainApi } from '../../utils/createApi';
import { Domain } from 'fs-api-client';
import { errorHandler } from '../../services/errorHandler';

export interface BillingPeriodInjectorProperties extends LoaderEnhancerProperties {
    fetchBillingPeriod: (showLoader: boolean) => Promise<BillingPeriod>;
    updateBillingPeriod: (billingPeriod: BillingPeriod) => Promise<void>;
    billingPeriod: BillingPeriod;
    errorMessage?: string;
}

interface BillingPeriodInjectorState {
    billingPeriod: BillingPeriod | null;
    fetching: boolean;
    errorMessage?: string;
}

let cache: BillingPeriod | null = null;
let injectorInstances: React.Component[] = [];

export function injectBillingPeriod<P extends BillingPeriodInjectorProperties>(component: React.ComponentType<P>) {
    const loaderName = 'billingPeriodInjector';
    let DynamicComponent: any = withLoader(component, loaderName);

    return class BillingPeriodInjector extends React.Component<Subtract<P, BillingPeriodInjectorProperties>, BillingPeriodInjectorState> {
        private domainIsCreated = false;
        private isCompMounted: boolean = false;

        constructor(props: P) {
            super(props);

            injectorInstances.push(this);

            this.fetchBillingPeriod = this.fetchBillingPeriod.bind(this);

            this.state = {
                billingPeriod: cache,
                fetching: !cache
            };
        }

        componentDidMount() {
            this.isCompMounted = true;
            if (!cache) {
                this.fetchBillingPeriod();
            }
        }

        componentWillUnmount() {
            this.isCompMounted = false;
            injectorInstances.splice(injectorInstances.indexOf(this), 1);
        }

        render() {
            return (
                <DynamicComponent
                    {...this.props}
                    loading={this.state.fetching}
                    fetchBillingPeriod={this.fetchBillingPeriod}
                    billingPeriod={this.state.billingPeriod}
                    errorMessage={this.state.errorMessage}
                />
            );
        }

        private async setFetching(fetching: boolean): Promise<void> {
            return new Promise((resolve, _reject) => {
                this.setState(
                    { fetching },
                    () => {
                        resolve();
                    }
                );
            });
        }

        private async fetchBillingPeriod(): Promise<void> {
            const domainIsCreated = await this.checkDomainIsCreated();
            if (!domainIsCreated) {
                await this.setFetching(false);
                return;
            }

            const billingPeriod = await this.getCurrentPeriod();
            cache = billingPeriod;
            if (this.isCompMounted) {
                this.setState(
                    {
                        billingPeriod,
                        fetching: false
                    },
                    () => {
                        this.updateAllInjectorInstances(billingPeriod);
                    }
                );
            }
        }

        private updateAllInjectorInstances(billingPeriod: BillingPeriod): void {
            for (let i = 0; i < injectorInstances.length; i++) {
                injectorInstances[i].setState({ billingPeriod });
            }
        }

        private async checkDomainIsCreated(): Promise<boolean> {
            if (this.domainIsCreated) {
                return true;
            }

            try {
                const domains = await this.getDomains();
                this.domainIsCreated = domains.length >= 1;

                return this.domainIsCreated;
            } catch (e) {
                errorHandler(e);
            }

            return false;
        }

        private async getDomains(): Promise<Domain[]> {
            const api = await createDomainApi();
            return (await api.getDomains()).body as Domain[];
        }

        private async getCurrentPeriod(): Promise<BillingPeriod> {
            const api = await createBillingApi();
            return (await api.getCurrentPeriod()).body as BillingPeriod;
        }
    };
}



// WEBPACK FOOTER //
// ./src/webportal/components/hocs/BillingPeriodInjector.tsx